/**
 * 跨adgroup层级，创意复制相关的一些操作
 * eg： 【投app的adgroup下的创意】复制到 【投website的adgroup】下
 * 需求文档：https://bytedance.feishu.cn/wiki/wikcneP1cAZCoNVEwZFIkW84bzb
 * 【建议以这个为准】checklist：https://bytedance.feishu.cn/wiki/wikcnOqZwf8LZqJdZD8f41VBf9X
 * 创编字段大全：https://bytedance.feishu.cn/sheets/shtcn0hWOEztVED205V08nVXvQh?sheet=MPAr1b
 */

import _ from 'lodash';
import { AUDIENCE_TYPE } from 'src/const/state/ad';
import {
  campaignObjectiveValue, contentType as EXTERNAL_TYPE, fallBackTypeEnum,
  contentType,
} from 'src/const/state/campaign';
import { OptimizeGoal } from 'src/const/state/target';
import { ADVANCED_CREATIVE_TYPE } from 'src/const/state/call-to-action';
import { CREATIVE_TYPE, deeplinkNotAllCountryListCode } from 'src/const/state/creative';
import { isDeeplinkWhite } from 'src/const/whiteList';


const isAppDownload = externalType => EXTERNAL_TYPE.app.includes(externalType);
const isWebsite = externalType => EXTERNAL_TYPE.website === externalType;
// 是否品牌且没有推广链接（表示纯曝光广告)
const isBrandNoExternalUrl = externalType => EXTERNAL_TYPE.brandPureExposure === externalType;
const isAndroidApp = newCreationData => newCreationData?.adgroupData?.external_type === contentType.app[0];
const isCountryInNotAllow = newCreationData => newCreationData?.adgroupData?.country?.some(code => deeplinkNotAllCountryListCode.includes(code));
const isAndroidAndSpecCountry = newCreationData => isAndroidApp(newCreationData) && !isCountryInNotAllow(newCreationData);
const isSupportDeeplink = newCreationData => isAndroidAndSpecCountry(newCreationData) || isDeeplinkWhite();
/**
 * Conversion
 * 1. adgroup选择website，清理所有app类型下才有的相关字段
 * 2. adgroup选择 app，则清理website才有的相关字段
 *
 * instant 创意：traffic/conversion 的website，仅选择tt版位，single video 下展示（page_id）
 *
 * @param {Object} param.creativeData 创意数据
 * @param {Number} param.adgroupData 复制新adgroup的external_type
 */
const resetCreativeDataInTraffic = ({
  creativeData,
  adgroupData,
}) => {
  if (isAppDownload(adgroupData.external_type)) {
    // fallbackType=0 or 不存在 表示traffic-app的创意没有启用deeplink，或者为 traffic-website复制到app
    // 复制traffic-website计划，改成traffic-app，创意deeplink有的勾选有的未勾选不一致的问题
    if (!creativeData.fallback_type) {
      creativeData.is_open_url = 0;
      creativeData.open_url = '';
    }

    if (fallBackTypeEnum.fallbackWeb !== creativeData.fallback_type) {
      // fallbackType为website，不能清空external_url
      creativeData.external_url = '';
    }
    // 清理website的相关字段
    creativeData.source = ''; // website下，创意的 display name 字段为 source,  app 下为 app_name 字段
    // instant的website创意 => app
    creativeData.landing_page_type = '';
    if (creativeData.creative_type !== CREATIVE_TYPE.LYNX_PLP) {
      creativeData.page_id = '';
    }
  } else if (isWebsite(adgroupData.external_type)) {
    // 清除app下的相关，删除deeplink，监测链接
    creativeData.app_name = '';
    creativeData.track_url = [];
    creativeData.action_track_url = [];

    // 清除fallback的相关字段
    creativeData.fallback_type = 0;
  }
  return creativeData;
};

/**
 * Conversion
 * 1. adgroup选择website，清理所有app类型下才有的相关字段
 * 2. adgroup选择 app，则清理website才有的相关字段
 *
 * instant 创意：traffic/conversion 的website，仅选择tt版位，single video 下展示（page_id）
 *
 * @param {Object} param.creativeData 创意数据
 * @param {Number} param.adgroupData 复制新adgroup的external_type
 */
const resetCreativeDataInConversion = ({
  creativeData,
  adgroupData,
}) => {
  if (isAppDownload(adgroupData.external_type)) {
    // 清理website的相关字段
    creativeData.external_url = '';
    creativeData.source = ''; // website下，创意的 display name 字段为 source,  app 下为 app_name 字段
    // instant的website创意 => app
    creativeData.landing_page_type = '';
    creativeData.page_id = '';
  } else if (isWebsite(adgroupData.external_type)) {
    // 清除app下的相关，删除deeplink，监测链接
    creativeData.app_name = '';
    creativeData.track_url = [];
    creativeData.action_track_url = [];
    // 清除deeplink和fallback_type 的值
    creativeData.is_open_url = 0; // 是否使用deeplink的开关
    creativeData.open_url = ''; // deeplink打开后，填的url对应字段
    creativeData.fallback_type = 0;
  }
  return creativeData;
};


/**
 * DPA情况比较多
 * 重定向：投放给之前已经有过行为的用户 =》拉活
 * 非重定向：投放给全新的用户 =》拉新
 *
 * 1. app
 *  1.1：清除website的字段
 *  1.2：重定向：清除非重定向的字段
 *  1.3：非重定向：清除 重定向的字段
 * 2. website
 *  2.1：清除app的相关字段
 *
 * @param {Object} param.creativeData 创意数据
 * @param {Object} param.adgroupData 创意复制到新的adgroup数据
 */
const resetCreativeDataInDpa = ({ creativeData, adgroupData }) => {
  const { external_type, dpa_local_audience } = adgroupData;

  // 重定向
  const isReTarget = dpa_local_audience === AUDIENCE_TYPE.RETARGET.value;
  const isWebsiteType = isWebsite(external_type);
  // 兼容下沉前的逻辑，下沉前app重定向501，app非重定向201， 下沉后只有201了，因此这里不是website就是app来判断
  if (!isWebsiteType) {
    // 清除website的字段
    creativeData.source = '';
    creativeData.external_url = '';

    if (isReTarget) {
      // 重定向：清理非重定向的字段
      creativeData.auto_open = 0;
      // fallback_type 为 0 （dpa website 类型的 website type 对应字段）
      // 或者（可能存在不设置的情况）则初始化为 0
      if (!creativeData.fallback_type) {
        creativeData.params_type = 0;
      }
    } else {
      // 非重定向：清理重定向的字段
      creativeData.fallback_type = 0;
      creativeData.params_type = 0;
      // app 重定向肯定有 deeplink，复制成非重默认为 2
      // 从 web 复制过来的保留初始化 0
      creativeData.auto_open = creativeData.is_open_url === 1 ? 2 : 0;
    }
  } else if (isWebsiteType) {
    // 清除app的字段
    creativeData.app_name = '';
    creativeData.track_url = [];
    creativeData.action_track_url = [];

    // 清除 dpa deeplink 相关字段
    creativeData.dpa_open_url_type = 0; // dpa app类型 的 deeplink下拉框对应字段
    creativeData.auto_open = 0; // 0表示deeplink, 2表示deffered deeplink, 默认为0
    creativeData.is_open_url = 0; // dpa deferred deeplink表单项的开关对应字段
    creativeData.open_url = ''; // dpa deeplink自定义link，对应的link字段
  }
  return creativeData;
};

/**
 * Engagement 中，如果优化目标是 【关注 = fans】，则没有cta 和open_url
 * @returns
 */
const resetCreativeDataEngagement = ({ creativeData, adgroupData }) => {
  if (adgroupData.optimize_goal === OptimizeGoal.fans) {
    creativeData.call_to_action = '';
    creativeData.call_to_action_id = '';
    creativeData.open_url = '';
    creativeData.is_open_url = '';
    creativeData.call_to_action_type = ADVANCED_CREATIVE_TYPE.default;
  }
  return creativeData;
};

/**
 * 品牌竞价广告，如果落地页为空，那么Cta应该也为空
 */
const resetCreativeDataReachAndVV = ({ creativeData, adgroupData }) => {
  // 解决track pixel复制换成track app external_url未清空
  if (adgroupData.ad_ref_app_id) {
    creativeData.external_url = '';
  }
  if (!creativeData.enableUrl && !adgroupData.ad_ref_app_id && !adgroupData.ad_ref_pixel_id) {
    creativeData.is_open_url = 0;
    creativeData.open_url = '';
    creativeData.call_to_action = '';
    creativeData.call_to_action_id = '';
    creativeData.call_to_action_type = ADVANCED_CREATIVE_TYPE.default;
  }
  return creativeData;
};

/**
 * 根据adgroup的数据，重置创意里面不符合当前条件的创意数据
 * eg: adgroup app下的 source
 *
 * @param {Object} creationData.creativeData 创意数据
 * @param {Object} creationData.adgroupData adgroup数据
 */
export const resetCreativeExtraKey = (creationData) => {
  const newCreationData = _.cloneDeep(creationData) || {};
  const {
    creativeData = {}, adgroupData = {}, isCopy = false, isCreate = false, shouldClearFallbackType = false,
  } = newCreationData;
  let newCreativeData = creativeData;

  if (adgroupData.struct_version === 1) {
    // 下沉后的处理
    switch (adgroupData.objective_type) {
      case campaignObjectiveValue.Traffic:
        newCreativeData = resetCreativeDataInTraffic(newCreationData);
        break;
      case campaignObjectiveValue.Conversion:
        newCreativeData = resetCreativeDataInConversion(newCreationData);
        break;
      case campaignObjectiveValue.DPA:
        newCreativeData = resetCreativeDataInDpa(newCreationData);
        break;
      case campaignObjectiveValue.Engagement:
        newCreativeData = resetCreativeDataEngagement(newCreationData);
        break;

      case campaignObjectiveValue.Reach:
      case campaignObjectiveValue.VideoView:
        newCreativeData = resetCreativeDataReachAndVV(newCreationData);
        break;

      default:
        break;
    }
    // 如果不支持deeplink
    if (!isSupportDeeplink(newCreationData) && ((shouldClearFallbackType && isCopy) || isCreate)) {
      newCreativeData.is_open_url = 0;
      newCreativeData.open_url = '';
      if (adgroupData.objective_type === campaignObjectiveValue.Traffic) {
        newCreativeData.fallback_type = 0;
        newCreativeData.external_url = '';
      }
    }
  } else if (adgroupData.struct_version === 0) {
    // 对非下沉计划，计划promotion为空时 那它下面的创意就得纯曝光 就得没有cta
    // 下沉前整体处理场景比较少，不按照Objective来各自处理
    if (isBrandNoExternalUrl(adgroupData.external_type)) {
      if (!adgroupData.external_url) {
        newCreativeData.call_to_action = '';
        newCreativeData.call_to_action_id = '';
      }
    }
  }
  return newCreativeData;
};
